import React, { useState } from "react";

import FreebetTag from "@components-shared/FreebetTag";
import { Button } from "@components/buttons";
import { UserPaymentTransactionLimitedHasDetails } from "@data-types/account-statement/UserPaymentTransactionLimitedHasDetails";
import TransactionReferenceText from "@features/account-statement/shared/TransactionReferenceText";
import { useAccountStatementStore } from "@state/hooks/account-statement/useAccountStatementStore";
import { DetailsTypeEnum } from "@state/stores/account-statement/AccountStatementStore";
import { SGUID_EMPTY_CONST, getDisplayedAmount } from "@utils";
import classNames from "classnames";
import { DateTime } from "luxon";
import { observer } from "mobx-react";

export default function AccountStatementTransactionItem({
	transaction,
}: PropType) {
	return (
		<tr
			key={transaction.numericalId}
			className="table__body__row table__row"
		>
			<td className="table__body__cell table__cell">
				{transaction.numericalId}
			</td>
			<td className="table__body__cell table__cell">
				{DateTime.fromISO(transaction.dateCreated).toLocaleString(
					DateTime.DATETIME_SHORT_WITH_SECONDS
				)}
			</td>
			<td className="table__body__cell table__cell">
				{transaction.transactionType?.name}
				<FreebetTag
					transactionTypeAbrv={transaction.transactionType?.abrv}
					amount={transaction.amount}
					isInlineTag
				/>
			</td>
			<td className="table__body__cell table__cell u-type--right u-type--wgt--medium">
				{getDisplayedAmount(
					transaction.amount,
					transaction.currency.displayCode
				)}
				<TransactionReferenceText
					referenceText={transaction.referenceText}
				/>
			</td>
			<td className="table__body__cell table__cell u-type--right">
				{getDisplayedAmount(
					transaction.netAmount,
					transaction.currency.displayCode
				)}
			</td>
			<td className="table__body__cell table__cell u-type--right">
				<TransactionHasDetails transaction={transaction} />
			</td>
		</tr>
	);
}

const TransactionHasDetails = observer(function TransactionHasDetails(
	props: PropType
) {
	const {
		transaction: {
			betSlipId,
			hasDetails,
			userPaymentTransactionId,
			transactionType,
		},
	} = props;
	const store = useAccountStatementStore();

	if (!hasDetails) {
		return null;
	}

	let transactionId = userPaymentTransactionId;
	let detailType: DetailsTypeEnum = DetailsTypeEnum.TransactionDetails;
	if (betSlipId != SGUID_EMPTY_CONST && betSlipId) {
		detailType = DetailsTypeEnum.BetSlipDetails;
		transactionId = betSlipId;
	}

	const [isHover, setIsHover] = useState(false);

	const iconClasses = classNames("u-icon--tny u-icon--caret--right", {
		"u-color--brand--primary": isHover,
	});

	if (
		transactionType?.abrv === "casino-live-to-casino-deposit" ||
		transactionType?.abrv === "casino-to-casino-live-withdrawal" ||
		transactionType?.abrv === "casino-live-to-casino-withdrawal" ||
		transactionType?.abrv === "casino-to-casino-live-deposit" ||
		transactionType?.abrv === "deposit-cash-to-digital-aircash" ||
		transactionType?.abrv === "withdrawal-cash-to-digital-aircash"
	) {
		return null;
	}

	return (
		<Button
			type="button"
			className="btn btn--square--reset btn--ghost"
			iconOnly={true}
			iconClassName={iconClasses}
			onClick={() =>
				store.accountStatementDetailsStore.fetchSlipDetails({
					detailType: detailType,
					transactionId: transactionId,
					transactionName:
						props.transaction.transactionType?.name ?? "",
					transactionTypeAbrv:
						props.transaction.transactionType?.abrv ?? "",
				})
			}
			onMouseEnter={() => setIsHover(true)}
			onMouseLeave={() => setIsHover(false)}
		/>
	);
});

type PropType = {
	transaction: UserPaymentTransactionLimitedHasDetails;
};
